import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    ModalDialog,
    SearchBar,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { ChevronDownIcon, SearchIcon } from './icons';

import type { CompetencyGroup } from '@/types/competency/Competency';

import { useGetCompetencyGroups } from '@/api/competency-services/competency';
import {
    useGetCompetencyCatalog,
    useImportCompetencies,
} from '@/api/competency-services/competency-catalog';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type ExploreCompetenciesModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const TURQUOISE = 'var(--color-primary)';

// The two-overlapping-circles glyph from the prototype header tile.
const LibraryGlyph = () => (
    <svg
        width={'32'}
        height={'32'}
        viewBox={'0 0 36 36'}
        fill={'none'}
        xmlns={'http://www.w3.org/2000/svg'}
    >
        <path
            d={
                'M21 23.9999C21 26.6549 19.845 29.0549 18 30.6899C16.41 32.1299 14.31 32.9999 12 32.9999C7.035 32.9999 3 28.9649 3 23.9999C3 19.8599 5.82 16.3499 9.63 15.3149C10.665 17.9249 12.885 19.9349 15.63 20.6849C16.38 20.8949 17.175 20.9999 18 20.9999C18.825 20.9999 19.62 20.8949 20.37 20.6849C20.775 21.7049 21 22.8299 21 23.9999Z'
            }
            stroke={TURQUOISE}
            strokeWidth={'1.5'}
            strokeLinecap={'round'}
            strokeLinejoin={'round'}
        />

        <path
            d={
                'M27 12C27 13.17 26.775 14.295 26.37 15.315C25.335 17.925 23.115 19.935 20.37 20.685C19.62 20.895 18.825 21 18 21C17.175 21 16.38 20.895 15.63 20.685C12.885 19.935 10.665 17.925 9.63 15.315C9.225 14.295 9 13.17 9 12C9 7.035 13.035 3 18 3C22.965 3 27 7.035 27 12Z'
            }
            stroke={TURQUOISE}
            strokeWidth={'1.5'}
            strokeLinecap={'round'}
            strokeLinejoin={'round'}
        />
    </svg>
);

const CheckGlyph = ({ className }: { className?: string }) => (
    <svg
        viewBox={'0 0 24 24'}
        fill={'none'}
        stroke={'currentColor'}
        strokeWidth={3}
        strokeLinecap={'round'}
        strokeLinejoin={'round'}
        className={className}
    >
        <path d={'M20 6 9 17l-5-5'} />
    </svg>
);

const filterGroupsBySearch = (
    groups: CompetencyGroup[],
    search: string,
    language: string,
): CompetencyGroup[] => {
    const query = search.trim().toLowerCase();
    if (!query) {
        return groups;
    }

    return groups
        .map((group) => ({
            ...group,
            competencies: group.competencies.filter((competency) => {
                const name = getTextByLanguage(competency.name, language).toLowerCase();
                const description = getTextByLanguage(
                    competency.description,
                    language,
                ).toLowerCase();

                return name.includes(query) || description.includes(query);
            }),
        }))
        .filter((group) => group.competencies.length > 0);
};

export const ExploreCompetenciesModal = ({ isOpen, onClose }: ExploreCompetenciesModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    // Only fetch the catalog while the modal is open.
    const { data: catalog, isFetching, isError } = useGetCompetencyCatalog(isOpen);
    const { data: companyGroupsData } = useGetCompetencyGroups();
    const { mutateAsync: importCompetencies, isPending: isImporting } = useImportCompetencies();

    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

    const groups = useMemo(() => (Array.isArray(catalog) ? catalog : []), [catalog]);

    const companyGroups = useMemo(
        () => (Array.isArray(companyGroupsData) ? companyGroupsData : []),
        [companyGroupsData],
    );

    // Catalog competency ids the company has already imported (its local copies
    // carry the original catalog id in `copiedFromCompetencyId`).
    const importedCatalogIds = useMemo(() => {
        const ids = new Set<string>();

        (companyGroups ?? []).forEach((group) => {
            group.competencies.forEach((competency) => {
                if (competency.copiedFromCompetencyId) {
                    ids.add(competency.copiedFromCompetencyId);
                }
            });
        });

        return ids;
    }, [companyGroups]);

    const filteredGroups = useMemo(
        () => filterGroupsBySearch(groups, searchText, language),
        [groups, searchText, language],
    );

    const toggleGroupExpanded = (groupId: string) => {
        setExpandedGroupIds((current) => {
            const next = new Set(current);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }

            return next;
        });
    };

    const toggleCompetency = (competencyId: string) => {
        // Already-imported competencies cannot be (re)selected.
        if (importedCatalogIds.has(competencyId)) {
            return;
        }

        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(competencyId)) {
                next.delete(competencyId);
            } else {
                next.add(competencyId);
            }

            return next;
        });
    };

    const handleClose = () => {
        setSearchText('');
        setSelectedIds(new Set());
        setExpandedGroupIds(new Set());
        onClose();
    };

    const onConfirm = async () => {
        if (selectedIds.size === 0) {
            handleClose();

            return;
        }

        try {
            await importCompetencies({ competencyIds: Array.from(selectedIds) });
            showSuccessToast({
                message: t('competencies.explore.success', { count: selectedIds.size }),
            });
            handleClose();
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            // Escape and the backdrop go through the same reset as the buttons, so a dismissed
            // search or selection never comes back on the next opening.
            onClose={handleClose}
            closeAriaLabel={t('common.close')}
            title={t('competencies.explore.title')}
            titleIcon={
                <span
                    className={
                        'bg-primary/10 mr-1 flex h-[55px] w-[55px] flex-shrink-0 items-center justify-center rounded-lg'
                    }
                >
                    <LibraryGlyph />
                </span>
            }
            subtitle={t('competencies.explore.subtitle')}
            size={'md'}
            className={
                'md:[&_[id$="-subtitle"]]:-mt-[24px] md:[&_[id$="-subtitle"]]:ml-[67px] md:[&_[id$="-title"]]:self-start'
            }
            radius={'lg'}
            bordered
            backdropBlur
            titleSize={'24px'}
            dataTestId={'competency-explore-modal'}
            headerExtra={
                <SearchBar
                    value={searchText}
                    onChange={(value) => setSearchText(value)}
                    placeholder={t('competencies.explore.searchPlaceholder')}
                    size={'lg'}
                    textSize={'md'}
                    className={'my-2'}
                    dataTestId={'explore-competencies-search'}
                />
            }
            footer={
                <div className={'flex items-center justify-between gap-2'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {t('competencies.explore.importedCountLabel', {
                            count: importedCatalogIds.size,
                        })}
                    </Text>

                    <div className={'flex gap-2'}>
                        <ButtonSecondary
                            onClick={handleClose}
                            disabled={isImporting}
                            size={'md'}
                            fitContent
                            dataTestId={'explore-competencies-cancel'}
                            className={'w-[82px]'}
                        >
                            {t('competencies.explore.close')}
                        </ButtonSecondary>

                        <ButtonGradientPrimary
                            onClick={onConfirm}
                            loading={isImporting}
                            disabled={selectedIds.size === 0}
                            size={'md'}
                            fitContent
                            dataTestId={'explore-competencies-import'}
                        >
                            {t('competencies.explore.importWithCount', { count: selectedIds.size })}
                        </ButtonGradientPrimary>
                    </div>
                </div>
            }
        >
            {isFetching && groups.length === 0 ? (
                <div className={'flex items-center justify-center py-12'}>
                    <OverlayLoader isLoading={isFetching} />
                </div>
            ) : isError ? (
                <p className={'text-error py-12 text-center text-sm'}>
                    {t('competencies.explore.loadError')}
                </p>
            ) : filteredGroups.length === 0 ? (
                <div
                    className={'flex flex-col items-center justify-center gap-3 py-12 text-center'}
                >
                    <SearchIcon className={'text-base-500/30 h-10 w-10'} />

                    <p className={'text-base-500 text-sm'}>{t('competencies.explore.empty')}</p>
                </div>
            ) : (
                <div className={'flex flex-col gap-2 pb-2'}>
                    {filteredGroups.map((group) => {
                        const isExpanded = expandedGroupIds.has(group.id);
                        const groupName = getTextByLanguage(group.name, language);
                        const groupDescription = getTextByLanguage(group.description, language);
                        const isGroupImported =
                            group.competencies.length > 0 &&
                            group.competencies.every((competency) =>
                                importedCatalogIds.has(competency.id),
                            );

                        return (
                            <div
                                key={group.id}
                                className={
                                    'border-base-100 overflow-hidden rounded-lg border bg-white'
                                }
                            >
                                <button
                                    type={'button'}
                                    onClick={() => toggleGroupExpanded(group.id)}
                                    aria-expanded={isExpanded}
                                    className={
                                        'hover:bg-base-25 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors'
                                    }
                                    data-testid={`explore-group-${group.id}`}
                                >
                                    <span
                                        className={
                                            'bg-primary/10 text-primary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold'
                                        }
                                    >
                                        {group.competencies.length}
                                    </span>

                                    <span className={'min-w-0 flex-1'}>
                                        <span className={'flex items-center gap-2'}>
                                            <span className={'text-navy text-base font-semibold'}>
                                                {groupName}
                                            </span>

                                            {isGroupImported && (
                                                <span
                                                    className={
                                                        'bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium'
                                                    }
                                                >
                                                    <CheckGlyph className={'h-3 w-3'} />

                                                    {t('competencies.explore.alreadyImported')}
                                                </span>
                                            )}
                                        </span>

                                        {groupDescription && (
                                            <span
                                                className={
                                                    'text-base-500 mt-0.5 block truncate text-xs'
                                                }
                                            >
                                                {groupDescription}
                                            </span>
                                        )}
                                    </span>

                                    <ChevronDownIcon
                                        className={`text-base-500 h-4 w-4 flex-shrink-0 transition-transform ${
                                            isExpanded ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>

                                {isExpanded && (
                                    <ul className={'flex flex-col gap-3 px-4 py-3'}>
                                        {group.competencies.map((competency) => {
                                            const isImported = importedCatalogIds.has(
                                                competency.id,
                                            );
                                            const isSelected = selectedIds.has(competency.id);
                                            const competencyName = getTextByLanguage(
                                                competency.name,
                                                language,
                                            );
                                            const competencyDescription = getTextByLanguage(
                                                competency.description,
                                                language,
                                            );

                                            return (
                                                <li key={competency.id}>
                                                    <button
                                                        type={'button'}
                                                        onClick={() =>
                                                            toggleCompetency(competency.id)
                                                        }
                                                        disabled={isImported}
                                                        className={
                                                            'flex w-full items-start gap-3 rounded-lg border px-4 py-4 text-left transition-colors ' +
                                                            (isImported
                                                                ? 'border-primary/30 bg-primary/5 cursor-not-allowed'
                                                                : isSelected
                                                                  ? 'border-primary/50 bg-primary/5'
                                                                  : 'border-base-100 hover:bg-base-25 bg-white')
                                                        }
                                                        data-testid={`explore-competency-${competency.id}`}
                                                    >
                                                        <span className={'pt-0.5'}>
                                                            <Checkbox
                                                                checked={isSelected || isImported}
                                                                disabled={isImported}
                                                                ariaLabel={competencyName}
                                                            />
                                                        </span>

                                                        <div className={'min-w-0 flex-1'}>
                                                            <div
                                                                className={
                                                                    'flex flex-wrap items-center gap-2'
                                                                }
                                                            >
                                                                <p
                                                                    className={
                                                                        'text-navy text-sm font-medium'
                                                                    }
                                                                >
                                                                    {competencyName}
                                                                </p>

                                                                {isImported && (
                                                                    <span
                                                                        className={
                                                                            'bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium'
                                                                        }
                                                                    >
                                                                        <CheckGlyph
                                                                            className={'h-3 w-3'}
                                                                        />

                                                                        {t(
                                                                            'competencies.explore.alreadyImported',
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {competencyDescription && (
                                                                <p
                                                                    className={
                                                                        'text-base-500 text-xs'
                                                                    }
                                                                >
                                                                    {competencyDescription}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </ModalDialog>
    );
};
